'use client';

import { createContext, ReactNode, useContext, useReducer } from 'react';
import { googleLogout } from '@react-oauth/google';
import { useTurnkey } from '@turnkey/sdk-react';
import { ReadOnlySession, Turnkey } from '@turnkey/sdk-browser';
import { useLocalStorage } from 'usehooks-ts';
import { AuthMethodType } from '@lit-protocol/constants';
import { AuthMethod, IRelayPKP } from '@lit-protocol/types';
import { createAccount } from '@turnkey/viem';
import { TurnkeySigner } from '@turnkey/solana';
import { decryptExportBundle, encryptPrivateKeyToBundle, generateP256KeyPair } from '@turnkey/crypto';
import IpfsHash from 'ipfs-only-hash';
import { ethers } from 'ethers';
import bs58 from 'bs58';
import { Account, createWalletClient, http, toHex } from 'viem';
import { Keypair } from '@solana/web3.js';
import { ExtendedProviderType } from '@shared/types/lit-protocol';
import { TDbEncryptedWallet } from '@shared/types/db/encrypted-wallets';
import { getUrls } from '@shared/utils/config/environment';
import { ChainId, PUBLIC_RPCS, WAGMI_CHAINS } from '@shared/utils/config/networks';
import { SuiWalletGenerator } from '@shared/services/helpers/sui/wallet-generation.service';
import { toast } from 'sonner';
import { createDbCloudWallet, updateDbCloudWallet } from '@/lib/db/DBCloudWalletHelpers';
import { createDBUser } from '@/lib/db/DBUserHelpers';
import { fetchIPFSData } from '@/lib/misc/fetchIPFSData';
import { decryptEncryptedWallet, signatureToDecryptEncryptedWallet } from '@/lib/auth/decryptEncryptedWallet';
import useGetWrappedWallet from '../useGetWrappedWallet';
import { createDBEncryptedWallet, fetchDBEncryptedWallets } from '@/lib/db/DBEncryptedWalletsHelpers';
import { checkMFA } from '@/lib/auth/mfa';
import { SESSION_LENGTHS } from '@/hooks/LitSessionContext/useLitSession';
import { getPKPSessionSig } from '@/lib/auth/sessionSig';
import { useSession } from '@/contexts/LitSessionProvider';
export type User = Awaited<ReturnType<Turnkey['getCurrentUser']>> & {
  readOnlySession?: ReadOnlySession;
  authSession?: any;
  turnKeySubOrg?: any;
  evmAddress?: string;
  solanaAddress?: string;
  suiAddress?: string;
};
type AuthActionType = {
  type: 'LOADING';
  payload: boolean;
} | {
  type: 'LOADINGLIT';
  payload: boolean;
} | {
  type: 'ERROR';
  payload: string;
} | {
  type: 'LOGIN';
  payload: User;
} | {
  type: 'LOGOUT';
  payload: User;
};
interface AuthState {
  loading: boolean;
  loadingLit: boolean;
  error: string;
  user: User | null;
}
const initialState: AuthState = {
  loading: false,
  loadingLit: false,
  error: '',
  user: null
};
function authReducer(state: AuthState, action: AuthActionType): AuthState {
  switch (action.type) {
    case 'LOADING':
      return {
        ...state,
        loading: action.payload
      };
    case 'LOADINGLIT':
      return {
        ...state,
        loadingLit: action.payload
      };
    case 'ERROR':
      return {
        ...state,
        error: action.payload,
        loading: false
      };
    case 'LOGIN':
    case 'LOGOUT':
      return {
        ...state,
        user: action.payload,
        loading: false,
        error: ''
      };
    default:
      return state;
  }
}
const AuthContext = createContext<{
  turnKeyState: AuthState;
  loginWithGoogle: (credential: string) => Promise<void>;
  getWalletClient: (chainId: number) => Promise<any>;
  exportWallet: (walletType: string) => Promise<string | null>;
  logoutTurnKey: () => Promise<void>;
  enableTurnKey: boolean;
  setEnableTurnKey: (boolean) => void;
}>({
  turnKeyState: initialState,
  loginWithGoogle: async () => {},
  getWalletClient: async () => {
    throw new Error('getWalletClient is not implemented');
  },
  exportWallet: async () => {
    throw new Error('getWalletClient is not implemented');
  },
  logoutTurnKey: async () => {
    throw new Error('logoutTurnKey is not implemented');
  },
  enableTurnKey: false,
  setEnableTurnKey: _ => {
    throw new Error('setEnableTurnKey is not implemented');
  }
});
export const TurnKeyAuthProvider = ({
  children
}: {
  children: ReactNode;
}) => {
  const TURNKEY_SESSION_LENGTH = `${12 * 60 * 60}`; //12 hours
  const [turnKeyState, dispatch] = useReducer(authReducer, initialState);
  const {
    turnkey,
    authIframeClient,
    getActiveClient
  } = useTurnkey();
  const [, setTurnKeySubOrg, removeTurnKeySubOrg] = useLocalStorage('genius-turnkey-suborg', null);
  const [enableTurnKey, setEnableTurnKey] = useLocalStorage('genius-enable-turnKey', null);
  const {
    litCachedMinSig,
    setLitCachedMinSig,
    setCipher1,
    setCipher2,
    setCipher3
  } = useGetWrappedWallet();
  const {
    clients,
    setAllPKPs,
    setPKP,
    setCurrentAuthMethod,
    setMFAInfo,
    setLitSession,
    sessionLength,
    setDecryptedWallets,
    setUsingTurnKey
  } = useSession();

  /* useEffect(() => {
    console.log('Turnket Lit initialised ', clients?.litNodeClient?.ready);
    if (clients?.litNodeClient?.ready && turnkey && authIframeClient?.iframePublicKey) {
      fetchUser(turnKeySubOrg);
    }
  }, [turnkey, clients?.litNodeClient?.ready, authIframeClient?.iframePublicKey]); */

  const fetchUser = async _turnKeySubOrg => {
    dispatch({
      type: 'LOADING',
      payload: true
    });
    try {
      if (turnkey) {
        const res = await fetch('/api/auth/turnkey-session');
        if (res.ok) {
          const data = await res.json();
          if (data?.exp > Date.now() / 1000) {
            if (_turnKeySubOrg) {
              let currentUser = await turnkey.getCurrentUser();
              if (currentUser?.session?.write) {
                if (currentUser?.session?.write.expiry < Date.now()) {
                  //Will reinject the session
                  const injectSuccess = await authIframeClient?.injectCredentialBundle(_turnKeySubOrg.oauthResponse.credentialBundle);
                  if (injectSuccess) {
                    await authIframeClient?.loginWithReadWriteSession(authIframeClient.iframePublicKey, TURNKEY_SESSION_LENGTH);
                    currentUser = await turnkey.getCurrentUser();
                  }
                }
                if (currentUser?.session?.write.expiry > Date.now()) {
                  let evmAddress = null;
                  const hasEthWallet = _turnKeySubOrg?.wallets?.[0]?.walletAddresses?.filter(add => {
                    return add.format == 'ADDRESS_FORMAT_ETHEREUM';
                  });
                  if (hasEthWallet?.length > 0) {
                    evmAddress = hasEthWallet[0].address;
                  }
                  let solanaAddress = null;
                  const hasSolanaWallet = _turnKeySubOrg?.wallets?.[0]?.walletAddresses?.filter(add => {
                    return add.format == 'ADDRESS_FORMAT_SOLANA';
                  });
                  if (hasSolanaWallet?.length > 0) {
                    solanaAddress = hasSolanaWallet[0].address;
                  }
                  let suiAddress = null;
                  const hasSuiWallet = _turnKeySubOrg?.wallets?.[0]?.walletAddresses?.filter(add => {
                    return add.format == 'ADDRESS_FORMAT_SUI';
                  });
                  if (hasSuiWallet?.length > 0) {
                    suiAddress = hasSuiWallet[0].address;
                  }
                  setPKP({
                    tokenId: null,
                    publicKey: null,
                    ethAddress: data?.user?.ethWallet,
                    firstName: 'ShuttleLabs Dev',
                    email: 'dev@shuttlelabs.xyz',
                    solanaAddress,
                    additionalEthAddress: evmAddress,
                    suiAddress,
                    isTurnkey: true
                  });
                  dispatch({
                    type: 'LOGIN',
                    payload: {
                      ...currentUser,
                      authSession: data,
                      turnKeySubOrg: _turnKeySubOrg,
                      evmAddress,
                      solanaAddress,
                      suiAddress
                    }
                  });
                  dispatch({
                    type: 'LOADING',
                    payload: false
                  });
                  return {
                    ...currentUser,
                    authSession: data,
                    turnKeySubOrg: _turnKeySubOrg
                  };
                } else {
                  throw new Error('Turnkey session expired');
                }
              }
            } else if (data?.user?.id_token && data?.user?.type == 'apple') {
              const credential = data?.user?.id_token;
              const sessionUpdateCall = await fetch('/api/auth/turnkey-session-update', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  removeTokenId: true
                })
              });
              if (sessionUpdateCall.ok) {
                dispatch({
                  type: 'LOADING',
                  payload: false
                });
                await loginWithApple(data, credential);
                return null;
              } else {
                throw new Error('Could not update session');
              }
            }
          } else {
            throw new Error('Session expired');
          }
        }
      }
    } catch (error) {
      logoutTurnKey();
      dispatch({
        type: 'ERROR',
        payload: error.message
      });
    } finally {
      dispatch({
        type: 'LOADING',
        payload: false
      });
    }
    return null;
  };
  const getWalletClient = async (chainId: number) => {
    if (chainId == ChainId.SOLANA) {
      if (turnKeyState?.user?.solanaAddress) {
        const turnkeyBrowserClient = await getActiveClient();
        const turnkeySigner = new TurnkeySigner({
          organizationId: turnKeyState.user.turnKeySubOrg.orgId!,
          client: turnkeyBrowserClient
        });
        return turnkeySigner;
      }
    } else if (chainId == ChainId.SUI) {
      if (turnKeyState?.user?.suiAddress) {
        return await getActiveClient();
      }
    } else {
      if (turnKeyState?.user?.evmAddress) {
        const turnkeyBrowserClient = await getActiveClient();
        const turnkeyAccount = await createAccount({
          client: turnkeyBrowserClient,
          organizationId: turnKeyState.user.turnKeySubOrg.orgId!,
          signWith: turnKeyState.user.evmAddress
        });
        const walletClient = createWalletClient({
          account: turnkeyAccount as Account,
          chain: WAGMI_CHAINS[chainId],
          transport: http(PUBLIC_RPCS[chainId])
        });
        return walletClient;
      }
    }
    return null;
  };
  const exportWallet = async (addressFormat: 'ADDRESS_FORMAT_ETHEREUM' | 'ADDRESS_FORMAT_SOLANA' | 'ADDRESS_FORMAT_SUI') => {
    const hasPrivateKey = turnKeyState.user.turnKeySubOrg.wallets[0].walletAddresses.filter(wAddress => wAddress.format == addressFormat);
    if (hasPrivateKey.length > 0) {
      const activeClient = await getActiveClient();
      const keyPair = generateP256KeyPair();
      const privateKey = keyPair.privateKey;
      const publicKey = keyPair.publicKeyUncompressed;
      const exportResponse = await activeClient.exportPrivateKey({
        privateKeyId: hasPrivateKey[0].privateKeyId,
        targetPublicKey: publicKey
      });
      const decryptedBundle = await decryptExportBundle({
        exportBundle: exportResponse.exportBundle,
        embeddedKey: privateKey,
        organizationId: turnKeyState.user.turnKeySubOrg.orgId,
        keyFormat: addressFormat == 'ADDRESS_FORMAT_SOLANA' ? 'SOLANA' : 'HEXADECIMAL',
        returnMnemonic: false
      });
      console.log('Turnkey: exportWallet', hasPrivateKey, decryptedBundle);
      return decryptedBundle;
    }
    return null;
  };
  const loginWithGoogle = async (credential: string) => {
    try {
      const authBackendResponse = await fetch('/api/auth/turnkey-google', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token: credential
        })
      });
      if (authBackendResponse.ok) {
        const stateUser = await loginWithOAuth(credential, 'Google Auth - Embedded Wallet');
        dispatch({
          type: 'LOADING',
          payload: true
        });
        const authMethod = {
          authMethodType: AuthMethodType.GoogleJwt,
          accessToken: credential
        };
        await tunKeyWedsLit(authMethod, stateUser);
        dispatch({
          type: 'LOADING',
          payload: false
        });
      } else {
        await logoutTurnKey();
        dispatch({
          type: 'ERROR',
          payload: 'Failed to authenticate with backend'
        });
      }
    } catch (error) {
      dispatch({
        type: 'ERROR',
        payload: error.message
      });
    }
  };
  const loginWithApple = async (authResult: any, credential: string) => {
    try {
      const stateUser = await loginWithOAuth(credential, 'Apple Auth - Embedded Wallet');
      dispatch({
        type: 'LOADING',
        payload: true
      });
      console.log('Turnkey: loginWithApple', {
        authResult,
        stateUser
      });
      const authMethod = {
        authMethodType: AuthMethodType.AppleJwt,
        accessToken: credential
      };
      await tunKeyWedsLit(authMethod, stateUser);
      dispatch({
        type: 'LOADING',
        payload: false
      });
    } catch (error) {
      dispatch({
        type: 'ERROR',
        payload: error.message
      });
    }
  };
  const loginWithOAuth = async (credential: string, providerName: string) => {
    dispatch({
      type: 'LOADING',
      payload: true
    });
    try {
      if (authIframeClient?.iframePublicKey) {
        const turnKeySubOrgsCall = await fetch('/api/turnkey/get-org-id', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            token: credential,
            providerName: providerName,
            targetPublicKey: authIframeClient?.iframePublicKey
          })
        });
        if (turnKeySubOrgsCall.ok) {
          const turnKeySubOrgsResult = await turnKeySubOrgsCall.json();
          if (turnKeySubOrgsResult.status == 'success' && turnKeySubOrgsResult.subOrgs.length > 0) {
            const _turnKeySubOrg = turnKeySubOrgsResult.subOrgs[0];
            setTurnKeySubOrg(_turnKeySubOrg);
            const injectSuccess = await authIframeClient?.injectCredentialBundle(turnKeySubOrgsResult.subOrgs[0].oauthResponse.credentialBundle);
            if (injectSuccess) {
              await authIframeClient?.loginWithReadWriteSession(authIframeClient.iframePublicKey, TURNKEY_SESSION_LENGTH);
              dispatch({
                type: 'LOADING',
                payload: true
              });
              return await fetchUser(_turnKeySubOrg);
            }
          } else {
            throw new Error('No sub orgs found');
          }
        } else {
          throw new Error('/api/turnkey/get-org-id failed');
        }
      } else {
        throw new Error('authIframeClient?.iframePublicKey is empty');
      }
    } catch (error: any) {
      logoutTurnKey();
      dispatch({
        type: 'ERROR',
        payload: error.message
      });
    } finally {
      dispatch({
        type: 'LOADING',
        payload: false
      });
    }
    return null;
  };
  const tunKeyWedsLit = async (authMethod: AuthMethod, stateUser?: any) => {
    try {
      const turnKeyIsReady = stateUser?.turnKeySubOrg?.wallets[0]?.users[0]?.userTag && stateUser?.turnKeySubOrg?.wallets[0]?.walletAddresses.filter(wAddress => wAddress.format == 'ADDRESS_FORMAT_SOLANA').length > 0 && stateUser?.turnKeySubOrg?.wallets[0]?.walletAddresses.filter(wAddress => wAddress.format == 'ADDRESS_FORMAT_ETHEREUM').length > 0 && stateUser?.turnKeySubOrg?.wallets[0]?.walletAddresses.filter(wAddress => wAddress.format == 'ADDRESS_FORMAT_SUI').length > 0;
      let needLit = true;
      if (turnKeyIsReady) {
        const turnKeySanityCall = await fetch('/api/turnkey/sanity');
        if (turnKeySanityCall.ok) {
          const turnKeySanity = await turnKeySanityCall.json();
          if (turnKeySanity.sanity) {
            needLit = false;
          }
        }
      }
      if (needLit) {
        let pkps = [];
        const provider = clients?.litAuthClients?.[stateUser?.authSession?.user?.type]?.provider;
        pkps = await provider.fetchPKPsThroughRelayer(authMethod);
        if (pkps?.length === 0) {
          //Its a new user so create wallets only in turnkey
          const activeClient = await getActiveClient();
          try {
            const newUser = await createDBUser({
              creation_auth_method: ExtendedProviderType.TurnKey,
              creation_auth_method_id: `turnkey_${stateUser.turnKeySubOrg.orgId}`,
              wallet_address: stateUser.turnKeySubOrg.wallets[0].accounts[0].address,
              email: stateUser.turnKeySubOrg.wallets[0].users[0].userEmail,
              first_name: stateUser.turnKeySubOrg.wallets[0].users[0].userName?.split('%20')[0],
              last_name: stateUser.turnKeySubOrg.wallets[0].users[0].userName?.split('%20')[1]
            });
            console.log('Turnkey: New user has been created', newUser);
            if (!stateUser.turnKeySubOrg.wallets[0].users[0]?.userTag) {
              const userTag = await activeClient.createUserTag({
                userTagName: stateUser.turnKeySubOrg.wallets[0].accounts[0]?.address,
                userIds: [stateUser.userId]
              });
              console.log('Turnkey: User tag has been created', userTag);
              stateUser.turnKeySubOrg.wallets[0].users[0].userTag = stateUser.turnKeySubOrg.wallets[0].accounts[0]?.address;
            }
            const dbCloudWalletResult = await createDbCloudWallet({
              eth_address: stateUser.turnKeySubOrg.wallets[0].accounts[0].address,
              pkp: null,
              creation_auth_method: ExtendedProviderType.TurnKey,
              creation_auth_method_id: `turnkey_${stateUser.turnKeySubOrg.orgId}`,
              lit_network: null,
              user_id: newUser.data['id'],
              creation_tx_hash: null,
              migrated_to_turnkey: true
            });
            console.log('Turnkey: New cloud wallet has been created', dbCloudWalletResult);
            const sessionUpdateCall = await fetch('/api/auth/turnkey-session-update', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                fromTurnkey: true
              })
            });
            if (sessionUpdateCall.ok) {
              let evmAddress = null;
              const hasEthAddress = stateUser?.turnKeySubOrg?.wallets[0]?.walletAddresses.filter(wAddress => wAddress.format == 'ADDRESS_FORMAT_ETHEREUM');
              if (hasEthAddress.length == 0) {
                const wallet = ethers.Wallet.createRandom();
                evmAddress = await importPrivateKeyOfAWallets(wallet.privateKey, 'HEXADECIMAL', 'EVM', stateUser.userId, stateUser.organization?.organizationId, 'CURVE_SECP256K1', ['ADDRESS_FORMAT_ETHEREUM']);
                console.log('Turnkey: EVM wallet imported with addreess', evmAddress);
              } else {
                evmAddress = hasEthAddress[0].address;
              }
              if (evmAddress) {
                const createdDbEncryptedEthWallet = await createDBEncryptedWallet({
                  pkp_eth_address: stateUser.turnKeySubOrg.wallets[0].accounts[0].address,
                  type: 'EVM',
                  address: evmAddress,
                  ciphertext: evmAddress,
                  data_to_encrypt_hash: '',
                  imported: false
                });
                if (createdDbEncryptedEthWallet.error) {
                  throw new Error('Could not Save evm encrypted wallet');
                }
                console.log('Turnkey: EVM wallet saved to db', createdDbEncryptedEthWallet);
                await (await fetch(`${getUrls().INDEXER_URL}/portfolio/start-tracking?wallet=${evmAddress}`))?.json();
              }
              const hasSolAddress = stateUser?.turnKeySubOrg?.wallets[0]?.walletAddresses.filter(wAddress => wAddress.format == 'ADDRESS_FORMAT_SOLANA');
              let solanaAddress = null;
              if (hasSolAddress.length == 0) {
                const keypair = Keypair.generate();
                solanaAddress = await importPrivateKeyOfAWallets(bs58.encode(keypair.secretKey), 'SOLANA', 'Solana', stateUser.userId, stateUser.organization?.organizationId, 'CURVE_ED25519', ['ADDRESS_FORMAT_SOLANA']);
                console.log('Turnkey: Solana wallet Imported with addreess', solanaAddress);
              } else {
                solanaAddress = hasSolAddress[0].address;
              }
              if (solanaAddress) {
                const createdDbEncryptedWallet = await createDBEncryptedWallet({
                  pkp_eth_address: stateUser.turnKeySubOrg.wallets[0].accounts[0].address,
                  type: 'Solana',
                  address: solanaAddress,
                  ciphertext: solanaAddress,
                  data_to_encrypt_hash: '',
                  imported: false
                });
                if (createdDbEncryptedWallet.error) {
                  throw new Error('Could not Save solana encrypted wallet');
                }
                console.log('Turnkey: Solana wallet saved to db', createdDbEncryptedWallet);
                await (await fetch(`${getUrls().INDEXER_URL}/portfolio/start-tracking?wallet=${solanaAddress}`))?.json();
              }
              const hasSuiAddress = stateUser?.turnKeySubOrg?.wallets[0]?.walletAddresses.filter(wAddress => wAddress.format == 'ADDRESS_FORMAT_SUI');
              let suiAddress = null;
              if (hasSuiAddress.length == 0) {
                const mnemonic = SuiWalletGenerator.generateMnemonic(24);
                const suiWallet = SuiWalletGenerator.generateWalletFromMnemonic(mnemonic);
                const privateKeyHex = toHex(suiWallet.privateKey);
                suiAddress = await importPrivateKeyOfAWallets(privateKeyHex, 'HEXADECIMAL', 'Sui', stateUser.userId, stateUser.organization?.organizationId, 'CURVE_ED25519', ['ADDRESS_FORMAT_SUI']);
                console.log('Turnkey: Sui wallet Imported with addreess', suiAddress);
              } else {
                suiAddress = hasSolAddress[0].address;
              }
              if (suiAddress) {
                const createdDbEncryptedEthWallet = await createDBEncryptedWallet({
                  pkp_eth_address: stateUser.turnKeySubOrg.wallets[0].users[0].userTag,
                  type: 'Sui',
                  address: suiAddress,
                  ciphertext: suiAddress,
                  data_to_encrypt_hash: '',
                  imported: false
                });
                if (createdDbEncryptedEthWallet.error) {
                  throw new Error('Could not Save Sui encrypted wallet');
                }
                console.log('Turnkey: Sui wallet saved to db', createdDbEncryptedEthWallet);
                await (await fetch(`${getUrls().INDEXER_URL}/portfolio/start-tracking?wallet=${suiAddress}`))?.json();
              }
              const importedWalletAddresses = await activeClient.getPrivateKeys({
                organizationId: stateUser.turnKeySubOrg.orgId
              });
              stateUser.turnKeySubOrg.wallets[0].walletAddresses = [];
              importedWalletAddresses.privateKeys.map(privateKey => {
                if (privateKey.addresses[0]) {
                  privateKey.addresses[0]['privateKeyId'] = privateKey.privateKeyId;
                  privateKey.addresses[0]['privateKeyName'] = privateKey.privateKeyName;
                  stateUser.turnKeySubOrg.wallets[0].walletAddresses = [...stateUser.turnKeySubOrg.wallets[0].walletAddresses, ...privateKey.addresses];
                }
              });
              setTurnKeySubOrg(stateUser.turnKeySubOrg);
              fetchUser(stateUser.turnKeySubOrg);
            }
          } catch (error) {
            console.log('Turnkey: Error in creating user/wallets', error);
          }
        } else {
          //Its an existing user in LIT, so migrate wallets in turnkey
          dispatch({
            type: 'LOADINGLIT',
            payload: true
          });
          const existingAddressesResponse = await fetch(`/api/auth/checkAccount?ethAddresses=${pkps?.map(x => x.ethAddress).join(',')}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          });
          const existingAddresses = await existingAddressesResponse.json();
          console.log('Turnkey: existingAddresses', existingAddresses);
          let pkp = null;
          if (existingAddresses?.existingEthAddress) {
            pkp = pkps?.find(x => x.ethAddress?.toLowerCase() === existingAddresses?.existingEthAddress?.toLowerCase());
          } else if (existingAddresses.error) {
            throw new Error(existingAddresses.error);
          }
          pkp['firstName'] = stateUser.turnKeySubOrg.wallets[0].users[0].userName?.split('%20')[0];
          pkp['lastName'] = stateUser.turnKeySubOrg.wallets[0].users[0].userName?.split('%20')[1];
          pkp['email'] = stateUser.turnKeySubOrg.wallets[0].users[0].userEmail;
          setAllPKPs(pkps);
          setCurrentAuthMethod(authMethod);
          setMFAInfo(null);
          const [_pkp, evmPrivateKey, solanaPrivateKey, suiPrivateKey] = await createLitSession(pkp, authMethod);
          setPKP(_pkp);
          if (_pkp && evmPrivateKey && solanaPrivateKey && suiPrivateKey) {
            await importPrivateKeysToTurnKey(_pkp, evmPrivateKey, solanaPrivateKey, suiPrivateKey, stateUser);
          } else {
            throw new Error('Could not initialize LIT');
          }
        }
      }
      const {
        data: encryptedWallets
      } = await fetchDBEncryptedWallets({});
      const hasEncryptedSolanaWallet: TDbEncryptedWallet | undefined = encryptedWallets?.find(x => x.type === 'Solana');
      const hasEncryptedEvmWallet: TDbEncryptedWallet | undefined = encryptedWallets?.find(x => x.type === 'EVM' && x.imported == false);
      const hasEncryptedSuiWallet: TDbEncryptedWallet | undefined = encryptedWallets?.find(x => x.type === 'Sui' && x.imported == false);
      setDecryptedWallets([hasEncryptedEvmWallet, hasEncryptedSolanaWallet, hasEncryptedSuiWallet]);
    } catch (error) {
      console.log('Turnkey: Error in tunKeyWedsLit', error);
      dispatch({
        type: 'ERROR',
        payload: error.message
      });
    } finally {
      dispatch({
        type: 'LOADINGLIT',
        payload: false
      });
    }
  };
  const importPrivateKeyOfAWallets = async (privateKey: string, keyFormat: string, name: string, turnkeyUserId: string, organizationId: string, curve: 'CURVE_SECP256K1' | 'CURVE_ED25519', addressFormats: ['ADDRESS_FORMAT_ETHEREUM' | 'ADDRESS_FORMAT_SOLANA' | 'ADDRESS_FORMAT_SUI']) => {
    const activeClient = await getActiveClient();
    const initImportResponse = await activeClient?.initImportPrivateKey({
      userId: turnkeyUserId
    });
    const encryptedBundle = await encryptPrivateKeyToBundle({
      privateKey: privateKey,
      keyFormat: keyFormat,
      importBundle: initImportResponse.importBundle,
      userId: turnkeyUserId,
      organizationId: organizationId
    });
    const importResponse = await activeClient?.importPrivateKey({
      userId: turnkeyUserId,
      privateKeyName: name,
      encryptedBundle: encryptedBundle,
      curve,
      addressFormats: addressFormats
    });
    return importResponse?.addresses[0]?.address;
  };
  const createLitSession = async (pkp: IRelayPKP, authMethod: AuthMethod): Promise<[IRelayPKP, string, string, string]> => {
    try {
      const sessionSigs = await getPKPSessionSig(pkp, authMethod, clients.litNodeClient, sessionLength || SESSION_LENGTHS['1 week']);
      setUsingTurnKey(true);
      setLitSession(sessionSigs);
      if (sessionSigs) {
        const sessionUpdateCall = await fetch('/api/auth/turnkey-session-update', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            sessionSig: sessionSigs
          })
        });
        if (!sessionUpdateCall.ok) {
          throw new Error('Could not update the session in backend');
        }
        const _mfaInfo = await checkMFA(pkp, sessionSigs, authMethod);
        setMFAInfo(_mfaInfo);
        const {
          data: encryptedWallets
        } = await fetchDBEncryptedWallets({});
        let hasEncryptedSolanaWallet: TDbEncryptedWallet | undefined = encryptedWallets?.find(x => x.type === 'Solana');
        let hasEncryptedEvmWallet: TDbEncryptedWallet | undefined = encryptedWallets?.find(x => x.type === 'EVM' && x.imported == false);
        let hasEncryptedSuiWallet: TDbEncryptedWallet | undefined = encryptedWallets?.find(x => x.type === 'Sui' && x.imported == false);
        console.log('Turnkey: encryptedWallets', {
          hasEncryptedSolanaWallet,
          hasEncryptedEvmWallet,
          hasEncryptedSuiWallet
        });
        let solanaAddress = null;
        let solanaCiphertext = null;
        let solanaDataToEncryptHash = null;
        let evmAddress = null;
        let evmCiphertext = null;
        let evmDataToEncryptHash = null;
        let suiAddress = null;
        let suiCiphertext = null;
        let suiDataToEncryptHash = null;
        if (!hasEncryptedSolanaWallet || !hasEncryptedEvmWallet || !hasEncryptedSuiWallet) {
          const code = await fetchIPFSData('Qmdn3AEqUf3WfS4L5XLdAtqm8eDvkLvHRDZJYfEW9vdTzf');
          const ipfsHash = await IpfsHash.of(code);
          let solanaWalletPromise;
          let evmWalletPromise;
          let suiWalletPromise;
          let wallet;
          let keypair;
          let suiWallet;
          if (!hasEncryptedEvmWallet) {
            wallet = ethers.Wallet.createRandom();
            evmAddress = wallet.address;
            const privateKey = wallet.privateKey;
            evmWalletPromise = clients.litNodeClient.executeJs({
              code: code,
              sessionSigs: sessionSigs,
              jsParams: {
                privateKey,
                encrypt: true,
                ethAddress: pkp?.ethAddress,
                ipfsHash
              }
            });
          }
          if (!hasEncryptedSolanaWallet) {
            keypair = Keypair.generate();
            solanaAddress = keypair.publicKey.toBase58();
            const privateKey = keypair.secretKey.toString();
            solanaWalletPromise = clients.litNodeClient.executeJs({
              code: code,
              sessionSigs: sessionSigs,
              jsParams: {
                privateKey,
                encrypt: true,
                ethAddress: pkp?.ethAddress,
                ipfsHash
              }
            });
          }
          if (!hasEncryptedSuiWallet) {
            const mnemonic = SuiWalletGenerator.generateMnemonic(24);
            suiWallet = SuiWalletGenerator.generateWalletFromMnemonic(mnemonic);
            suiAddress = suiWallet.address;
            suiWalletPromise = clients.litNodeClient.executeJs({
              code: code,
              sessionSigs: sessionSigs,
              jsParams: {
                privateKey: mnemonic,
                encrypt: true,
                ethAddress: pkp?.ethAddress,
                ipfsHash
              }
            });
          }
          const [newEvmWalletRes, newSolanaWalletRes, newSuiWalletRes] = await Promise.all([evmWalletPromise, solanaWalletPromise, suiWalletPromise]);
          if (newEvmWalletRes) {
            const parsedRes = JSON.parse(newEvmWalletRes.response);
            evmCiphertext = parsedRes.ciphertext;
            evmDataToEncryptHash = parsedRes.dataToEncryptHash;
            const createdDbEncryptedWallet = await createDBEncryptedWallet({
              pkp_eth_address: pkp?.ethAddress,
              type: 'EVM',
              address: evmAddress,
              ciphertext: evmCiphertext,
              data_to_encrypt_hash: evmDataToEncryptHash,
              imported: false
            });
            if (createdDbEncryptedWallet.error) {
              throw new Error('Could not Save evm encrypted wallet');
            }
            hasEncryptedEvmWallet = createdDbEncryptedWallet?.data;
            console.log('Turnkey: evm wallet saved to db', createdDbEncryptedWallet);
            await (await fetch(`${getUrls().INDEXER_URL}/portfolio/start-tracking?wallet=${evmAddress}`))?.json();
          }
          if (newSolanaWalletRes) {
            const parsedRes = JSON.parse(newSolanaWalletRes.response);
            console.log('parsedRes: ', parsedRes);
            solanaCiphertext = parsedRes.ciphertext;
            solanaDataToEncryptHash = parsedRes.dataToEncryptHash;
            const createdDbEncryptedWallet = await createDBEncryptedWallet({
              pkp_eth_address: pkp?.ethAddress,
              type: 'Solana',
              address: solanaAddress,
              ciphertext: solanaCiphertext,
              data_to_encrypt_hash: solanaDataToEncryptHash,
              imported: false
            });
            if (createdDbEncryptedWallet.error) {
              throw new Error('Could not Save Solana encrypted wallet');
            }
            hasEncryptedSolanaWallet = createdDbEncryptedWallet?.data;
            console.log('Turnkey: solana wallet saved to db', createdDbEncryptedWallet);
            await (await fetch(`${getUrls().INDEXER_URL}/portfolio/start-tracking?wallet=${solanaAddress}`))?.json();
          }
          if (newSuiWalletRes) {
            const parsedRes = JSON.parse(newSuiWalletRes.response);
            suiCiphertext = parsedRes.ciphertext;
            suiDataToEncryptHash = parsedRes.dataToEncryptHash;
            const createDbEncryptedWallet = await createDBEncryptedWallet({
              pkp_eth_address: pkp?.ethAddress,
              type: 'Sui',
              address: suiAddress,
              ciphertext: suiCiphertext,
              data_to_encrypt_hash: suiDataToEncryptHash,
              imported: false
            });
            if (createDbEncryptedWallet.error) {
              throw new Error('Could not Save Sui encrypted wallet');
            }
            hasEncryptedSuiWallet = createDbEncryptedWallet?.data;
            await (await fetch(`${getUrls().INDEXER_URL}/portfolio/start-tracking?wallet=${suiAddress}`))?.json();
          }
        }
        setDecryptedWallets([hasEncryptedEvmWallet, hasEncryptedSolanaWallet, hasEncryptedSuiWallet]);
        pkp = {
          ...pkp,
          solanaAddress: solanaAddress || hasEncryptedSolanaWallet?.address,
          ciphertext: solanaCiphertext || hasEncryptedSolanaWallet?.ciphertext,
          dataToEncryptHash: solanaDataToEncryptHash || hasEncryptedSolanaWallet?.data_to_encrypt_hash,
          additionalEthAddress: evmAddress || hasEncryptedEvmWallet?.address,
          additionalEthAddressCiphertext: evmCiphertext || hasEncryptedEvmWallet?.ciphertext,
          additionalEthAddressDataToEncryptHash: evmDataToEncryptHash || hasEncryptedEvmWallet?.data_to_encrypt_hash,
          suiAddress: suiAddress || hasEncryptedSuiWallet?.address,
          suiCiphertext: suiCiphertext || hasEncryptedSuiWallet?.ciphertext,
          suiDataToEncryptHash: suiDataToEncryptHash || hasEncryptedSuiWallet?.data_to_encrypt_hash
        };
        const cacheCallSig = await signatureToDecryptEncryptedWallet({
          PKP: pkp as IRelayPKP,
          litSession: sessionSigs,
          litNodeClient: clients.litNodeClient,
          litCachedMinSig,
          setLitCachedMinSig
        });
        const evmPrivateKeyPromise = decryptEncryptedWallet({
          PKP: pkp as IRelayPKP,
          litSession: sessionSigs,
          litNodeClient: clients.litNodeClient,
          encryptedWallet: hasEncryptedEvmWallet,
          signature: cacheCallSig,
          setCipher1,
          setCipher2,
          setCipher3
        });
        const solanaPrivateKeyPromise = decryptEncryptedWallet({
          PKP: pkp as IRelayPKP,
          litSession: sessionSigs,
          litNodeClient: clients.litNodeClient,
          encryptedWallet: hasEncryptedSolanaWallet,
          signature: cacheCallSig,
          setCipher1,
          setCipher2,
          setCipher3
        });
        const suiPrivateKeyPromise = decryptEncryptedWallet({
          PKP: pkp as IRelayPKP,
          litSession: sessionSigs,
          litNodeClient: clients.litNodeClient,
          encryptedWallet: hasEncryptedSuiWallet,
          signature: cacheCallSig,
          setCipher1,
          setCipher2,
          setCipher3
        });
        const [evmPrivateKeyWallet, solanaPrivateKeyWallet, suiPrivateKeyWallet] = await Promise.all([evmPrivateKeyPromise, solanaPrivateKeyPromise, suiPrivateKeyPromise]);
        if (evmPrivateKeyWallet.error || solanaPrivateKeyWallet.error || suiPrivateKeyWallet.error) {
          console.log('Turnkey: ERROR in setUpDecryptedWallets', {
            evmPrivateKeyWallet,
            solanaPrivateKeyWallet,
            suiPrivateKeyWallet
          });
          throw new Error('Error in decrypting wallets');
        }
        const evmPrivateKey = evmPrivateKeyWallet?.privateKey as string;
        const solanaPrivateKey = solanaPrivateKeyWallet?.privateKey as string;
        const suiPrivateKey = toHex(suiPrivateKeyWallet?.privateKey) as string;
        return [pkp, evmPrivateKey, solanaPrivateKey, suiPrivateKey];
      }
    } catch (err) {
      toast.error('Something went wrong while creating session, try again');
      console.log('Turnkey: Error in createLitSession ', err);
    }
    return [null, null, null, null];
  };
  const importPrivateKeysToTurnKey = async (pkp: IRelayPKP, evmPrivateKey: string, solanaPrivateKey: string, suiPrivateKey: string, stateUser: any): Promise<void> => {
    let ethAddressToImport = false;
    let solAddressToImport = false;
    let suiAddressToImport = false;
    stateUser?.turnKeySubOrg?.wallets.map(wallet => {
      ethAddressToImport = wallet?.walletAddresses.filter(wAddress => wAddress.address.toLowerCase() == pkp.additionalEthAddress.toLowerCase()).length == 0;
      solAddressToImport = wallet?.walletAddresses.filter(wAddress => wAddress.address == pkp.solanaAddress).length == 0;
      suiAddressToImport = wallet?.walletAddresses.filter(wAddress => wAddress.address.toLowerCase() == pkp.suiAddress.toLowerCase()).length == 0;
    });
    console.log('Turnkey: importPrivateKeysToTurnKey', {
      ethAddressToImport,
      evmPrivateKey,
      solAddressToImport,
      solanaPrivateKey,
      suiAddressToImport,
      suiPrivateKey
    });
    const activeClient = await getActiveClient();
    if (!stateUser.turnKeySubOrg.wallets[0].users[0]?.userTag) {
      try {
        const userTag = await activeClient.createUserTag({
          userTagName: pkp.ethAddress,
          userIds: [stateUser?.userId]
        });
        stateUser.turnKeySubOrg.wallets[0].users[0].userTag = pkp.ethAddress;
        setTurnKeySubOrg(stateUser.turnKeySubOrg);
        console.log('Turnkey: User tag has been created', userTag);
      } catch (error) {
        console.log('Turnkey: Error in creating user tag', error);
      }
    }
    if (ethAddressToImport || solAddressToImport || suiAddressToImport) {
      if (ethAddressToImport) {
        const _evmAddress = await importPrivateKeyOfAWallets(evmPrivateKey, 'HEXADECIMAL', 'EVM', stateUser.userId, stateUser.organization?.organizationId, 'CURVE_SECP256K1', ['ADDRESS_FORMAT_ETHEREUM']);
        console.log('Turnkey: EVM wallet Imported with addreess', _evmAddress);
      }
      if (solAddressToImport) {
        const _solanaAddress = await importPrivateKeyOfAWallets(solanaPrivateKey, 'SOLANA', 'Solana', stateUser.userId, stateUser.organization?.organizationId, 'CURVE_ED25519', ['ADDRESS_FORMAT_SOLANA']);
        console.log('Turnkey: Solana wallet Imported with addreess', _solanaAddress);
      }
      if (suiAddressToImport) {
        const _usianaAddress = await importPrivateKeyOfAWallets(suiPrivateKey, 'HEXADECIMAL', 'Sui', stateUser.userId, stateUser.organization?.organizationId, 'CURVE_ED25519', ['ADDRESS_FORMAT_SUI']);
        console.log('Turnkey: Sui wallet Imported with addreess', _usianaAddress);
      }
      const importedWalletAddresses = await activeClient.getPrivateKeys({
        organizationId: stateUser.turnKeySubOrg.orgId
      });
      stateUser.turnKeySubOrg.wallets[0].walletAddresses = [];
      importedWalletAddresses.privateKeys.map(privateKey => {
        if (privateKey.addresses[0]) {
          privateKey.addresses[0]['privateKeyId'] = privateKey.privateKeyId;
          privateKey.addresses[0]['privateKeyName'] = privateKey.privateKeyName;
          stateUser.turnKeySubOrg.wallets[0].walletAddresses = [...stateUser.turnKeySubOrg.wallets[0].walletAddresses, ...privateKey.addresses];
        }
      });
      setTurnKeySubOrg(stateUser.turnKeySubOrg);
    }
    await updateDbCloudWallet({
      eth_address: pkp.ethAddress,
      migrated_to_turnkey: true
    });
    fetchUser(stateUser.turnKeySubOrg);
  };
  const logoutTurnKey = async () => {
    dispatch({
      type: 'LOADING',
      payload: true
    });
    await turnkey?.logoutUser();
    await fetch('/api/auth/turnkey-logout', {
      method: 'POST'
    });
    googleLogout();
    removeTurnKeySubOrg();
    //removeEnableTurnKey();
    dispatch({
      type: 'LOGOUT',
      payload: null
    });
  };
  return <AuthContext.Provider value={{
    turnKeyState,
    loginWithGoogle,
    getWalletClient,
    exportWallet,
    logoutTurnKey,
    enableTurnKey,
    setEnableTurnKey
  }} data-sentry-element="unknown" data-sentry-component="TurnKeyAuthProvider" data-sentry-source-file="useTurnKeyAuth.tsx">
      {children}
    </AuthContext.Provider>;
};
export const useTurnKeyAuth = () => useContext(AuthContext);